/**
 * CoffeeOrderPage
 * ───────────────
 * A focused café-style pre-order flow.
 *
 *   1. Pick a coffee from a dropdown (Flat White, Cappuccino, Latte, …)
 *   2. Choose milk type (Full cream, Oat, Almond, …)
 *   3. Pick size (Small / Regular / Large) — price updates live
 *   4. Optional extras chips (extra shot, syrups, decaf)
 *   5. ± Qty stepper + Add to cart  (clears the extras + qty after)
 *   6. **Recommended Add-ons** section — Cakes / Biscuits / Pancakes /
 *      Scones (with cream + jam toppings) / Toasties. Each block has a
 *      dropdown, ± qty stepper and "Add" button. Only categories the
 *      tenant actually stocks are shown.
 *   7. Cart panel — global cart (so the header ShoppingBag badge updates).
 *      Each line has its own ± stepper and a remove button.
 *   8. Ready-by dropdown (ASAP + 5-min slots in the next 90 mins)
 *   9. Pickup vs Delivery (address appears for Delivery)
 *  10. Place order  → POST /api/orders → success screen + cart.clear()
 *
 * The page reads its catalogue from /api/menu — no hard-coded items.
 * Route: /order
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Coffee, Minus, Plus, ShoppingBag, Trash2, Check, Clock,
  Cake, Cookie, Croissant, Sandwich, UtensilsCrossed, Sparkles,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, money, formatApiError } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { useCart } from "@/context/CartContext";

/* Broad drink-type taxonomy used to bucket items in the order builder.
 * Each type captures one or more category-name patterns AND a friendly
 * label for the tab chip row. Items that match none of the regexes fall
 * back to "other-drinks" so nothing ever disappears silently. */
const DRINK_TYPES = [
  { key: "coffee", label: "Coffee", rx: /coffee|espresso|latte|cappuccino|mocha|chai|matcha|hot drink/i },
  { key: "tea",    label: "Tea",    rx: /\btea\b/i },
  { key: "other",  label: "Other Drinks", rx: /iced|cold brew|smoothie|shake|crush|juice|drink/i },
];

function drinkTypeFor(item, catNameById) {
  const catName = catNameById[item?.category_id] || item?.category_name || "";
  const hay = `${catName} ${item?.name || ""}`;
  for (const t of DRINK_TYPES) if (t.rx.test(hay)) return t.key;
  return "other";
}

/* Milk options — same list whether the café stocks them or not. */
const MILK_OPTIONS = [
  { value: "full-cream",   label: "Full cream" },
  { value: "skim",         label: "Skim" },
  { value: "lactose-free", label: "Lactose-free" },
  { value: "oat",          label: "Oat" },
  { value: "almond",       label: "Almond" },
  { value: "soy",          label: "Soy" },
  { value: "coconut",      label: "Coconut" },
  { value: "no-milk",      label: "No milk" },
];

/* Optional extras the customer can stack onto any drink. */
const COFFEE_EXTRAS = [
  { value: "extra-shot",     label: "Extra shot",     price: 1.0 },
  { value: "decaf",          label: "Decaf",          price: 0.0 },
  { value: "syrup-vanilla",  label: "Vanilla syrup",  price: 0.8 },
  { value: "syrup-caramel",  label: "Caramel syrup",  price: 0.8 },
  { value: "syrup-hazelnut", label: "Hazelnut syrup", price: 0.8 },
];

/* Toppings offered for Scones. Each is priced individually so the
 * customer can pick one, both, or none. */
const SCONE_TOPPINGS = [
  { value: "cream", label: "Cream", price: 1.5 },
  { value: "jam",   label: "Jam",   price: 1.0 },
];

const SIZE_KEY_ORDER = [
  "sml", "small", "reg", "regular", "med", "medium", "lrg", "large",
  "cup", "pot", "mug",
  "6oz", "12oz", "16oz",
  "14oz", "20oz",
  "glass-r", "glass-l",
  "light", "full",
];
const SIZE_LABELS = {
  sml: "Small", small: "Small",
  reg: "Regular", regular: "Regular", med: "Medium", medium: "Medium",
  lrg: "Large", large: "Large",
  cup: "Cup (dine in)", pot: "Pot", mug: "Mug (dine in)",
  "6oz":  "6oz takeaway",
  "12oz": "12oz takeaway",
  "16oz": "16oz takeaway",
  "14oz": "14oz takeaway",
  "20oz": "20oz takeaway",
  "glass-r": "Regular glass (dine in)",
  "glass-l": "Large glass (dine in)",
  light: "Light", full: "Full",
};

/* ── Allergen detection ─────────────────────────────────────────────────
 * Surfaces the 9 most-common allergens that cause real harm (Anaphylaxis
 * Australia's priority list). We match against the item's `tags` field
 * first (authoritative when set by the restaurant), then fall back to
 * keyword sniffing in the name + description. False positives are far
 * preferable to false negatives here — over-warning is annoying;
 * under-warning is dangerous.
 *
 * Each rule yields one short, parent-friendly label. We never list more
 * than 4 labels per item to keep the chip row readable.
 */
const ALLERGEN_RULES = [
  { label: "nuts",      tags: ["nuts", "tree-nuts"],     rx: /\b(almond|walnut|pecan|hazelnut|cashew|pistachio|macadamia|brazil|nut|nuts|pesto|nutella|frangipane|praline|marzipan)\b/i },
  { label: "peanuts",   tags: ["peanut", "peanuts"],     rx: /\b(peanut|peanuts|satay)\b/i },
  { label: "dairy",     tags: ["dairy"],                 rx: /\b(milk|butter|cream|cheese|yog(h?)urt|ricotta|mozzarella|parmesan|bega|halloumi|mascarpone|buttermilk|whey|ghee)\b/i },
  { label: "gluten",    tags: ["gluten", "wheat"],       rx: /\b(wheat|gluten|flour|bread|sourdough|pasta|biscuit|cookie|cake|pancake|toast|toastie|wrap|panini|sandwich|brioche|scone|muffin|oat|oats|bun|crumb)\b/i },
  { label: "egg",       tags: ["egg", "eggs"],           rx: /\b(egg|eggs|benedict|mayo|aioli|frittata|omelette)\b/i },
  { label: "soy",       tags: ["soy", "soya"],           rx: /\b(soy|soya|tofu|edamame|tempeh|miso)\b/i },
  { label: "sesame",    tags: ["sesame"],                rx: /\b(sesame|tahini|hummus|halva)\b/i },
  { label: "shellfish", tags: ["shellfish", "crustacea"], rx: /\b(prawn|shrimp|crab|lobster|crayfish|scallop|mussel|oyster|clam)\b/i },
  { label: "fish",      tags: ["fish"],                  rx: /\b(salmon|tuna|cod|anchov(?:y|ies)|sardine|mackerel|barramundi|snapper|fish)\b/i },
];

function detectAllergens(item) {
  if (!item) return [];
  const found = new Set();
  const tagsLower = (item.tags || []).map((t) => String(t).toLowerCase());
  const haystack = `${item.name || ""}\n${item.description || ""}\n${item.long_description || ""}`;
  for (const rule of ALLERGEN_RULES) {
    if (rule.tags.some((t) => tagsLower.includes(t))) {
      found.add(rule.label);
      continue;
    }
    if (rule.rx.test(haystack)) found.add(rule.label);
  }
  // Respect explicit safe tags — strip the matching allergen out.
  if (tagsLower.includes("dairy-free") || tagsLower.includes("vegan")) found.delete("dairy");
  if (tagsLower.includes("gluten-free")) found.delete("gluten");
  if (tagsLower.includes("nut-free")) { found.delete("nuts"); found.delete("peanuts"); }
  if (tagsLower.includes("vegan")) found.delete("egg");
  return Array.from(found).slice(0, 4);
}

// Lists used by the dietary-filter chip UI (positive + negative).
const DIET_TAGS_POSITIVE = [
  { value: "vegan",         label: "Vegan" },
  { value: "vegetarian",    label: "Vegetarian" },
  { value: "pescatarian",   label: "Pescatarian" },
  { value: "gluten-free",   label: "Gluten-free" },
  { value: "dairy-free",    label: "Dairy-free" },
];
const ALLERGEN_LABELS = [
  "nuts", "peanuts", "dairy", "gluten", "egg", "soy", "sesame", "shellfish", "fish",
];

function passesDietRequire(item, requiredTags) {
  if (!requiredTags || requiredTags.length === 0) return true;
  const tags = (item?.tags || []).map((t) => String(t).toLowerCase());
  // ALL required tags must be present.
  return requiredTags.every((rt) => tags.includes(rt));
}

function passesDietHide(item, hiddenAllergens) {
  if (!hiddenAllergens || hiddenAllergens.length === 0) return true;
  const found = detectAllergens(item);
  return !found.some((a) => hiddenAllergens.includes(a));
}

function sizeKeysFromItem(item) {
  const keys = Object.keys(item?.prices || {});
  if (keys.length === 0) return [];
  return keys.sort((a, b) => SIZE_KEY_ORDER.indexOf(a) - SIZE_KEY_ORDER.indexOf(b));
}

function unitPriceFor(item, sizeKey) {
  const raw = item?.prices?.[sizeKey];
  if (raw !== undefined && raw !== null && raw !== "") return Number(raw) || 0;
  return Number(item?.price) || 0;
}

function buildPickupSlots() {
  const now = new Date();
  const start = new Date(now.getTime() + 10 * 60 * 1000);
  start.setMinutes(start.getMinutes() + ((5 - (start.getMinutes() % 5)) % 5), 0, 0);
  const slots = [{ value: "ASAP", label: "ASAP — as fast as the queue allows" }];
  for (let i = 0; i < 18; i++) {
    const t = new Date(start.getTime() + i * 5 * 60 * 1000);
    const hh = t.getHours();
    const mm = String(t.getMinutes()).padStart(2, "0");
    const hr12 = ((hh + 11) % 12) + 1;
    const ampm = hh >= 12 ? "pm" : "am";
    const label = `${hr12}:${mm} ${ampm}`;
    const value = `${String(hh).padStart(2, "0")}:${mm}`;
    slots.push({ value, label });
  }
  return slots;
}

/* Recommended Add-on groups. Each group inspects the tenant's menu and
 * collects matching items by category-name regex (preferred) or item
 * name regex (fallback). Empty groups are hidden so a café without
 * scones never sees an empty Scones panel. */
const ADDON_GROUPS = [
  { key: "brunch",   label: "Brunch & Plates", Icon: UtensilsCrossed, catRx: /brunch|classic|plate|bigger|benn|italian|italiano|frittata/i, nameRx: /benn|brunch|frittata|bolognese|focaccia|pizza|pasta|steak|bistecca/i },
  { key: "burgers",  label: "Burgers",         Icon: Sandwich,        catRx: /burger/i, nameRx: /burger|cheeseburger|b\.l\.a\.t|schnit/i },
  { key: "kids",     label: "Kids",            Icon: Cookie,          catRx: /kid/i,    nameRx: /nugget|toastie.*kid|kid/i },
  { key: "cakes",    label: "Cakes & Pastries", Icon: Cake,           catRx: /cake|pastr|slice|bread/i, nameRx: /cake|slice|bread|muffin|brownie/i },
  { key: "biscuits", label: "Biscuits",         Icon: Cookie,         catRx: /biscuit|cookie/i,         nameRx: /biscuit|cookie|anzac/i },
  { key: "pancakes", label: "Pancakes",         Icon: UtensilsCrossed, catRx: /pancake|waffle/i,        nameRx: /pancake|waffle/i },
  { key: "scones",   label: "Scones",           Icon: Croissant,      catRx: /scone/i,                  nameRx: /scone/i, toppings: true },
  { key: "toasties", label: "Toasties",         Icon: Sandwich,       catRx: /toast|panini|sandwich|wrap/i, nameRx: /toastie|toast|panini|sandwich|wrap/i },
];

export default function CoffeeOrderPage() {
  const { business } = useBusiness();
  const navigate = useNavigate();
  const cart = useCart();

  const [menu, setMenu] = useState([]);          // full tenant menu
  const [cats, setCats] = useState([]);          // categories (id → name)
  const [coffees, setCoffees] = useState([]);    // filtered beverage items
  const [loading, setLoading] = useState(true);
  const [drinkType, setDrinkType] = useState("coffee");  // Coffee | Tea | Other

  // Builder state — the drink being configured.
  const [coffeeId, setCoffeeId] = useState("");
  const [milk, setMilk] = useState("full-cream");
  const [sizeKey, setSizeKey] = useState("");
  const [qty, setQty] = useState(1);
  const [extras, setExtras] = useState([]);

  // Per-group addon state (selected itemId, qty, toppings for scones).
  const [addonState, setAddonState] = useState({});

  // ── Phase 25.18 dietary filters ──────────────────────────────────────
  // Two complementary axes per user request:
  //   * `dietRequire` = positive — only show items tagged Vegan / Pescatarian / GF / DF
  //   * `dietHide`    = negative — hide items containing allergens (uses
  //                     the same detector as <AllergyChips />)
  // Both filters compose; selecting nothing means "show everything".
  const [dietRequire, setDietRequire] = useState([]); // values from DIET_TAGS_POSITIVE
  const [dietHide, setDietHide] = useState([]);       // values from ALLERGEN_LABELS

  // Fulfilment state
  const [readyAt, setReadyAt] = useState("ASAP");
  const [fulfilment, setFulfilment] = useState("pickup");
  const [address, setAddress] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!business?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: menuData }, { data: catData }] = await Promise.all([
          api.get("/menu"),
          api.get("/categories").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setMenu(menuData || []);
        setCats(catData || []);
      } catch (_e) {
        if (!cancelled) { setMenu([]); setCats([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [business?.id]);

  // catId → name lookup (kept stable for both filters + display).
  const catNameById = useMemo(() => {
    const m = {};
    (cats || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [cats]);

  // Beverages — any item whose category is `beverage` kind, falls back to
  // the drink-type taxonomy (coffee/tea/other) for tenants without `kind`
  // set. The drink-type tab then narrows the dropdown.
  useEffect(() => {
    const beverageCatIds = new Set(
      (cats || []).filter((c) => (c.kind || "").toLowerCase() === "beverage").map((c) => c.id)
    );
    const all = (menu || []).filter((m) => {
      if (m?.show_on_pickup === false) return false;  // curated out by admin
      if (beverageCatIds.has(m.category_id)) return true;
      const catName = catNameById[m.category_id] || m.category_name || "";
      if (/coffee|tea|drink|smoothie|shake|juice|crush|iced/i.test(catName)) return true;
      const tags = (m.tags || []).map((x) => String(x).toLowerCase());
      if (tags.some((t) => t.includes("coffee") || t.includes("drink") || t.includes("tea"))) return true;
      return Object.keys(m.prices || {}).length > 1;
    });
    const byType = all.filter((m) => drinkTypeFor(m, catNameById) === drinkType);
    setCoffees(byType);
    if (byType.length && !byType.some((x) => x.id === coffeeId)) {
      setCoffeeId(byType[0].id);
      const sk = sizeKeysFromItem(byType[0]);
      setSizeKey(sk[0] || "");
    }
  }, [menu, cats, catNameById, drinkType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group menu items into the Recommended Add-on buckets, applying any
  // active dietary positive (require) + negative (hide) filters.
  const addonItemsByGroup = useMemo(() => {
    const grouped = {};
    // Exclude EVERY beverage from the add-on list so iced drinks/teas
    // don't sneak into "From our menu" while the customer is on the
    // Coffee tab.
    const beverageCatIds = new Set(
      (cats || []).filter((c) => (c.kind || "").toLowerCase() === "beverage").map((c) => c.id)
    );
    ADDON_GROUPS.forEach((g) => {
      const items = (menu || []).filter((m) => {
        if (m?.show_on_pickup === false) return false;  // curated out by admin
        if (beverageCatIds.has(m.category_id)) return false;
        const catName = catNameById[m.category_id] || m.category_name || "";
        if (/coffee|tea|drink|smoothie|shake|juice|crush|iced/i.test(catName)) return false;
        const catMatch = g.catRx.test(catName) || g.nameRx.test(m.name || "");
        if (!catMatch) return false;
        if (!passesDietRequire(m, dietRequire)) return false;
        if (!passesDietHide(m, dietHide)) return false;
        return true;
      });
      grouped[g.key] = items;
    });
    return grouped;
  }, [menu, cats, catNameById, dietRequire, dietHide]);

  const slots = useMemo(() => buildPickupSlots(), []);

  const selected = coffees.find((i) => i.id === coffeeId) || null;
  const sizeKeys = sizeKeysFromItem(selected);

  // Pull Milk + Extras from the item's own addons array when present —
  // this is the source of truth set in the backend per product. Falls
  // back to the platform defaults so legacy items keep working.
  const itemMilkOptions = useMemo(() => {
    const grp = (selected?.addons || []).find((g) => /milk/i.test(g?.name || ""));
    if (!grp || !Array.isArray(grp.options) || grp.options.length === 0) {
      // Item has no Milk group → return defaults ONLY if the item has no
      // addons at all (legacy data). Otherwise the item explicitly opts
      // out of milk (e.g. tea) and we should render nothing.
      if ((selected?.addons || []).length === 0) return MILK_OPTIONS;
      return [];
    }
    return grp.options
      .filter((o) => o.enabled !== false)
      .map((o) => ({ value: o.name, label: o.name, price: Number(o.price) || 0 }));
  }, [selected]);

  const itemExtraOptions = useMemo(() => {
    // Flatten EVERY non-milk addon group on the item — this covers the
    // Coffee Commune model where coffee carries both a Regular (60¢) and
    // Specialty ($3) add-on sheet, and burgers/bennies/build-your-own
    // carry their own bespoke groups.
    const groups = (selected?.addons || []).filter(
      (g) => !/milk/i.test(g?.name || "") && Array.isArray(g?.options) && g.enabled !== false,
    );
    if (groups.length === 0 && (selected?.addons || []).length === 0) return COFFEE_EXTRAS;
    const opts = [];
    groups.forEach((g) => {
      (g.options || []).filter((o) => o.enabled !== false).forEach((o) => {
        opts.push({
          value: `${g.name}::${o.name}`,
          label: o.name,
          price: Number(o.price) || 0,
          group: g.name,
        });
      });
    });
    return opts;
  }, [selected]);

  // Reset milk / extras when the item changes so a stale "Oat" doesn't
  // hang around on a tenant that doesn't stock it.
  useEffect(() => {
    if (!selected) return;
    const milkVals = itemMilkOptions.map((m) => m.value);
    if (!milkVals.includes(milk)) setMilk(milkVals[0] || "full-cream");
    setExtras((cur) => cur.filter((v) => itemExtraOptions.some((e) => e.value === v)));
  }, [coffeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep sizeKey valid when the customer switches drinks.
  useEffect(() => {
    if (!selected) return;
    const keys = sizeKeysFromItem(selected);
    if (!keys.includes(sizeKey)) setSizeKey(keys[0] || "");
  }, [coffeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const extrasTotal = extras.reduce(
    (s, k) => s + (itemExtraOptions.find((x) => x.value === k)?.price || 0),
    0,
  );
  const milkSurcharge = itemMilkOptions.length > 0
    ? (itemMilkOptions.find((m) => m.value === milk)?.price || 0)
    : 0;
  const coffeeUnitPrice = selected ? unitPriceFor(selected, sizeKey) + extrasTotal + milkSurcharge : 0;

  function toggleExtra(v) {
    setExtras((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  }

  function addCoffeeToCart() {
    if (!selected) return;
    if (!sizeKey && sizeKeys.length > 0) {
      toast.error("Please pick a size");
      return;
    }
    const milkOpt = itemMilkOptions.length > 0 ? itemMilkOptions.find((m) => m.value === milk) : null;
    const sizeLabel = SIZE_LABELS[sizeKey] || sizeKey || "Regular";
    const selectedAddons = [
      { name: `Size: ${sizeLabel}`, price: 0 },
      ...(milkOpt ? [{ name: `Milk: ${milkOpt.label}`, price: Number(milkOpt.price) || 0 }] : []),
      ...extras.map((v) => {
        const e = itemExtraOptions.find((x) => x.value === v);
        return { name: e?.label || v, price: e?.price || 0 };
      }),
    ];
    cart.addLine({
      item_id: selected.id,
      name: selected.name,
      base_price: unitPriceFor(selected, sizeKey),
      quantity: qty,
      selected_addons: selectedAddons,
      notes: "",
      calories: Number(selected.calories) || 0,
      tags: selected.tags || [],
      price_column: sizeKey || "",
      price_label: sizeLabel,
    });
    toast.success(`${qty}× ${selected.name} added`);
    // Reset to a clean builder state so "Add more to order" is friction-free.
    setQty(1);
    setExtras([]);
  }

  function updateAddonGroup(key, patch) {
    setAddonState((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), ...patch } }));
  }

  function addAddonToCart(group) {
    const st = addonState[group.key] || {};
    const itemId = st.itemId || addonItemsByGroup[group.key]?.[0]?.id;
    const item = (addonItemsByGroup[group.key] || []).find((i) => i.id === itemId);
    if (!item) {
      toast.error(`Pick a ${group.label.toLowerCase().replace(/s$/, "")} first`);
      return;
    }
    const addonQty = Math.max(1, Number(st.qty || 1));
    const selectedAddons = [];
    if (group.toppings) {
      const ts = (st.toppings || []).map((tv) => SCONE_TOPPINGS.find((t) => t.value === tv)).filter(Boolean);
      ts.forEach((t) => selectedAddons.push({ name: `Topping: ${t.label}`, price: t.price }));
    }
    cart.addLine({
      item_id: item.id,
      name: item.name,
      base_price: Number(item.price) || 0,
      quantity: addonQty,
      selected_addons: selectedAddons,
      notes: "",
      calories: Number(item.calories) || 0,
      tags: item.tags || [],
      price_column: "",
      price_label: "",
    });
    toast.success(`${addonQty}× ${item.name} added`);
    // Reset just this group's qty + toppings so adding "1 more" is one click away.
    updateAddonGroup(group.key, { qty: 1, toppings: [] });
  }

  async function placeOrder() {
    if (cart.lines.length === 0) {
      toast.error("Add at least one item to your cart");
      return;
    }
    if (!contact.name || !contact.phone) {
      toast.error("Please add a name + phone so we can find you");
      return;
    }
    if (fulfilment === "delivery" && !address.trim()) {
      toast.error("Please add a delivery address");
      return;
    }
    if (fulfilment === "dine-in" && !tableNumber.trim()) {
      toast.error("Please enter your table number");
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const pickupTime = readyAt === "ASAP" ? "ASAP" : readyAt;
      // EmailStr rejects .local — synth a sentinel address on our domain.
      const fallbackEmail = `${contact.phone.replace(/\D/g, "") || "guest"}@noemail.hustleplate.app`;
      const payload = {
        customer_name: contact.name,
        customer_email: contact.email || fallbackEmail,
        customer_phone: contact.phone,
        pickup_date: `${yyyy}-${mm}-${dd}`,
        pickup_time: pickupTime,
        notes: [
          `Fulfilment: ${
            fulfilment === "delivery" ? `Delivery → ${address}`
            : fulfilment === "dine-in" ? `Dine in → Table ${tableNumber}`
            : "Pickup"
          }`,
          notes,
        ].filter(Boolean).join("\n"),
        payment_method: "pay_on_pickup",
        origin_url: window.location.origin,
        items: cart.lines.map((l) => ({
          item_id: l.item_id,
          quantity: l.quantity,
          selected_addon_options: (l.selected_addons || []).map((a) => a.name),
          notes: "",
          price_column: l.price_column || "",
        })),
      };
      const { data } = await api.post("/orders", payload);
      setConfirmed(data.order || data);
      toast.success("Order placed!");
      cart.clear();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const venue = business?.business_name || "the café";

  if (confirmed) {
    return (
      <div
        data-testid="coffee-order-success"
        className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
      >
        <div className="rounded-2xl border border-[#E8E5DF] bg-white p-8 md:p-12 text-center">
          <div className="h-14 w-14 rounded-full bg-[#5A7247] text-white mx-auto flex items-center justify-center">
            <Check className="h-6 w-6" strokeWidth={2} />
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl mt-5">Order received.</h1>
          <p className="text-[#5C625E] mt-3">
            We&apos;ve sent your order to the bar. Ready{" "}
            <strong className="text-[#121614]">{readyAt === "ASAP" ? "as soon as the queue allows" : `at ${readyAt}`}</strong>
            {" · "}{
              fulfilment === "delivery" ? "delivering to " + address
              : fulfilment === "dine-in" ? `dine in · Table ${tableNumber}`
              : "pickup at " + venue
            }.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button
              data-testid="order-success-home"
              onClick={() => navigate("/")}
              className="bg-[#121614] hover:bg-black text-white rounded-full px-6"
            >
              Back to home
            </Button>
            <Button
              data-testid="order-success-again"
              variant="outline"
              onClick={() => { setConfirmed(null); }}
              className="rounded-full px-6"
            >
              Order another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="coffee-order-page" className="bg-[#FDFBF7]">
      {/* Header */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-6">
        <div className="flex items-center gap-3 text-[#5A7247] text-xs uppercase tracking-[0.15em] font-bold">
          <Coffee className="h-4 w-4" strokeWidth={1.5} /> Pre-order coffee
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-tight">
          Skip the queue at {venue}.
        </h1>
        <p className="mt-3 text-[#5C625E] max-w-2xl">
          Build your drink, lock in a ready time, and roll up to the counter (or have us bring it to you).
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Builder + Recommended add-ons */}
        <div className="lg:col-span-3 space-y-6">
          {/* Coffee builder */}
          <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8 space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">1 · Pick a drink</Label>
              <div data-testid="drink-type-tabs" className="mt-2 flex flex-wrap gap-2">
                {DRINK_TYPES.map((t) => {
                  const on = drinkType === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`drink-type-${t.key}`}
                      onClick={() => setDrinkType(t.key)}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                        on
                          ? "bg-[#121614] text-white border-[#121614]"
                          : "bg-white text-[#121614] border-[#E8E5DF] hover:bg-[#F3F0EA]"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <Select value={coffeeId} onValueChange={setCoffeeId}>
                <SelectTrigger data-testid="coffee-select" className="mt-3 h-12 rounded-xl">
                  <SelectValue placeholder={loading ? "Loading…" : "Please select a drink"} />
                </SelectTrigger>
                <SelectContent>
                  {coffees.length === 0 && (
                    <SelectItem disabled value="__none__" data-testid="coffee-option-none">
                      No drinks in this category yet
                    </SelectItem>
                  )}
                  {coffees.map((i) => (
                    <SelectItem key={i.id} value={i.id} data-testid={`coffee-option-${i.id}`}>
                      {i.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected?.description && (
                <p className="text-xs text-[#5C625E] mt-2">{selected.description}</p>
              )}
              <AllergyChips item={selected} testidPrefix="coffee" />
            </div>

            <div className={`grid gap-4 ${itemMilkOptions.length > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
              {itemMilkOptions.length > 0 && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">2 · Milk</Label>
                  <Select value={milk} onValueChange={setMilk}>
                    <SelectTrigger data-testid="milk-select" className="mt-2 h-12 rounded-xl">
                      <SelectValue placeholder="Please select milk" />
                    </SelectTrigger>
                    <SelectContent>
                      {itemMilkOptions.map((m) => (
                        <SelectItem
                          key={m.value}
                          value={m.value}
                          data-testid={`milk-option-${m.value.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {m.label}{m.price > 0 ? ` · +${money(m.price)}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                  {itemMilkOptions.length > 0 ? "3 · Size" : "2 · Size"}
                </Label>
                <Select value={sizeKey} onValueChange={setSizeKey} disabled={sizeKeys.length === 0}>
                  <SelectTrigger data-testid="size-select" className="mt-2 h-12 rounded-xl">
                    <SelectValue placeholder={sizeKeys.length === 0 ? "—" : "Please select size"} />
                  </SelectTrigger>
                  <SelectContent>
                    {sizeKeys.length === 0 && (
                      <SelectItem disabled value="__no_size__" data-testid="size-option-none">
                        No size variants
                      </SelectItem>
                    )}
                    {sizeKeys.map((k) => (
                      <SelectItem key={k} value={k} data-testid={`size-option-${k}`}>
                        {SIZE_LABELS[k] || k} · {money(unitPriceFor(selected, k))}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">Extras (optional)</Label>
              {itemExtraOptions.length === 0 ? (
                <p className="mt-2 text-xs text-[#5C625E] italic">No extras available for this item.</p>
              ) : (() => {
                // Bucket options by their original addon-group name so the
                // customer sees the "60¢" and "$3" tiers clearly separated.
                const buckets = [];
                const seen = new Map();
                itemExtraOptions.forEach((o) => {
                  const k = o.group || "Extras";
                  if (!seen.has(k)) { seen.set(k, buckets.length); buckets.push({ name: k, options: [] }); }
                  buckets[seen.get(k)].options.push(o);
                });
                return (
                  <div className="mt-2 space-y-3">
                    {buckets.map((b) => (
                      <div key={b.name}>
                        {buckets.length > 1 && (
                          <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#5C625E] mb-1.5">
                            {b.name}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {b.options.map((e) => {
                            const on = extras.includes(e.value);
                            const slug = e.value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                            return (
                              <button
                                type="button"
                                key={e.value}
                                onClick={() => toggleExtra(e.value)}
                                data-testid={`extra-${slug}`}
                                className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                                  on
                                    ? "bg-[#C05746] text-white border-[#C05746]"
                                    : "bg-white text-[#121614] border-[#E8E5DF] hover:bg-[#F3F0EA]"
                                }`}
                              >
                                {e.label}{e.price > 0 ? ` · +${money(e.price)}` : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                  {itemMilkOptions.length > 0 ? "4 · Quantity" : "3 · Quantity"}
                </Label>
                <QtyStepper
                  value={qty}
                  onChange={setQty}
                  testidPrefix="qty"
                />
              </div>

              <Button
                data-testid="add-to-cart"
                onClick={addCoffeeToCart}
                disabled={!selected}
                className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-8 h-12"
              >
                Add to cart · {money(coffeeUnitPrice * qty)}
              </Button>
            </div>
          </div>

          {/* Recommended add-ons */}
          {(ADDON_GROUPS.some((g) => (addonItemsByGroup[g.key] || []).length > 0)
            || dietRequire.length > 0 || dietHide.length > 0) && (
            <div
              data-testid="addon-panel"
              className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8 space-y-5"
            >
              <div className="flex items-center gap-2 text-[#C05746]">
                <Sparkles className="h-4 w-4" strokeWidth={2} />
                <h2 className="font-serif-display text-2xl text-[#121614]">From our menu.</h2>
              </div>
              <p className="text-sm text-[#5C625E] -mt-2">
                Add anything below to the same order — bagels, brunch plates, pastries and more.
              </p>

              {/* Dietary filters — positive (Show only) + negative (Hide). */}
              <div data-testid="diet-filters" className="space-y-3 rounded-xl border border-[#E8E5DF] bg-[#FDFBF7] p-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#5A7247] mb-2">
                    Show only
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DIET_TAGS_POSITIVE.map((t) => {
                      const on = dietRequire.includes(t.value);
                      return (
                        <button
                          type="button"
                          key={t.value}
                          data-testid={`diet-show-${t.value}`}
                          onClick={() => setDietRequire((cur) =>
                            on ? cur.filter((x) => x !== t.value) : [...cur, t.value]
                          )}
                          className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                            on
                              ? "bg-[#5A7247] text-white border-[#5A7247]"
                              : "bg-white text-[#121614] border-[#E8E5DF] hover:bg-[#F3F0EA]"
                          }`}
                        >
                          {on ? "✓ " : "+ "}{t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-700 mb-2">
                    Hide items containing
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ALLERGEN_LABELS.map((a) => {
                      const on = dietHide.includes(a);
                      return (
                        <button
                          type="button"
                          key={a}
                          data-testid={`diet-hide-${a}`}
                          onClick={() => setDietHide((cur) =>
                            on ? cur.filter((x) => x !== a) : [...cur, a]
                          )}
                          className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                            on
                              ? "bg-amber-200 text-amber-900 border-amber-300"
                              : "bg-white text-[#121614] border-[#E8E5DF] hover:bg-[#F3F0EA]"
                          }`}
                        >
                          {on ? "✕ " : "+ "}{a}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {(dietRequire.length > 0 || dietHide.length > 0) && (
                  <button
                    type="button"
                    data-testid="diet-clear"
                    onClick={() => { setDietRequire([]); setDietHide([]); }}
                    className="text-xs text-[#5C625E] hover:text-[#C05746] underline underline-offset-2"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              {ADDON_GROUPS.every((g) => (addonItemsByGroup[g.key] || []).length === 0) && (
                <p
                  data-testid="addon-empty"
                  className="text-sm text-[#5C625E] italic text-center py-6"
                >
                  No add-ons match your filters. Try clearing one above.
                </p>
              )}

              {ADDON_GROUPS.map((group) => {
                const groupItems = addonItemsByGroup[group.key] || [];
                if (groupItems.length === 0) return null;
                const st = addonState[group.key] || {};
                const selectedItemId = st.itemId || groupItems[0].id;
                const item = groupItems.find((i) => i.id === selectedItemId) || groupItems[0];
                const groupQty = Math.max(1, Number(st.qty || 1));
                const groupToppings = st.toppings || [];
                const Icon = group.Icon;
                const toppingsTotal = group.toppings
                  ? groupToppings.reduce((s, tv) => s + (SCONE_TOPPINGS.find((t) => t.value === tv)?.price || 0), 0)
                  : 0;
                const groupUnit = (Number(item?.price) || 0) + toppingsTotal;

                return (
                  <div
                    key={group.key}
                    data-testid={`addon-group-${group.key}`}
                    className="border border-[#E8E5DF] rounded-xl p-4 md:p-5 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 md:gap-4 items-end"
                  >
                    <div className="md:col-span-3 flex items-center gap-2 text-[#121614]">
                      <Icon className="h-4 w-4 text-[#5A7247]" strokeWidth={1.75} />
                      <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">{group.label}</span>
                    </div>

                    <div className="min-w-0">
                      <Select
                        value={selectedItemId}
                        onValueChange={(v) => updateAddonGroup(group.key, { itemId: v })}
                      >
                        <SelectTrigger
                          data-testid={`addon-select-${group.key}`}
                          className="h-11 rounded-xl"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {groupItems.map((i) => (
                            <SelectItem
                              key={i.id}
                              value={i.id}
                              data-testid={`addon-option-${group.key}-${i.id}`}
                            >
                              {i.name} · {money(Number(i.price) || 0)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {item?.description && (
                        <p className="text-xs text-[#5C625E] mt-1.5 line-clamp-1">{item.description}</p>
                      )}
                      <AllergyChips item={item} testidPrefix={`addon-${group.key}`} />
                      {group.toppings && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {SCONE_TOPPINGS.map((t) => {
                            const on = groupToppings.includes(t.value);
                            return (
                              <button
                                type="button"
                                key={t.value}
                                data-testid={`addon-topping-${group.key}-${t.value}`}
                                onClick={() => {
                                  const next = on
                                    ? groupToppings.filter((x) => x !== t.value)
                                    : [...groupToppings, t.value];
                                  updateAddonGroup(group.key, { toppings: next });
                                }}
                                className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                                  on
                                    ? "bg-[#5A7247] text-white border-[#5A7247]"
                                    : "bg-white text-[#121614] border-[#E8E5DF] hover:bg-[#F3F0EA]"
                                }`}
                              >
                                {on ? "✓ " : "+ "}{t.label} · +{money(t.price)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <QtyStepper
                      value={groupQty}
                      onChange={(n) => updateAddonGroup(group.key, { qty: n })}
                      testidPrefix={`addon-qty-${group.key}`}
                    />

                    <Button
                      data-testid={`addon-add-${group.key}`}
                      onClick={() => addAddonToCart(group)}
                      className="bg-[#121614] hover:bg-black text-white rounded-full px-5 h-11 whitespace-nowrap"
                    >
                      Add · {money(groupUnit * groupQty)}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cart + Fulfilment */}
        <aside className="lg:col-span-2 space-y-6">
          <CartPanel cart={cart} />

          <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#5A7247]" />
                <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">5 · Ready by</Label>
              </div>
              <Select value={readyAt} onValueChange={setReadyAt}>
                <SelectTrigger data-testid="ready-at" className="mt-2 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {slots.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`slot-${s.value}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">6 · How</Label>
              <RadioGroup
                value={fulfilment}
                onValueChange={setFulfilment}
                className="mt-2 grid grid-cols-3 gap-2"
              >
                {[
                  { v: "pickup",   l: "Pickup" },
                  { v: "dine-in",  l: "Dine in" },
                  { v: "delivery", l: "Delivery" },
                ].map(({ v, l }) => (
                  <label
                    key={v}
                    htmlFor={`fulf-${v}`}
                    data-testid={`fulfilment-${v}`}
                    className={`cursor-pointer rounded-xl border px-3 py-3 flex items-center gap-2 transition-colors ${
                      fulfilment === v
                        ? "border-[#C05746] bg-[#C05746]/5"
                        : "border-[#E8E5DF] hover:bg-[#F3F0EA]"
                    }`}
                  >
                    <RadioGroupItem value={v} id={`fulf-${v}`} />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
              </RadioGroup>
              {fulfilment === "delivery" && (
                <Input
                  data-testid="delivery-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Delivery address — street, suburb, postcode"
                  className="mt-2 h-11 rounded-xl"
                />
              )}
              {fulfilment === "dine-in" && (
                <Input
                  data-testid="dine-in-table"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  placeholder="Table number — find the number on your table"
                  className="mt-2 h-11 rounded-xl"
                />
              )}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Input
                data-testid="contact-name"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                placeholder="Your name"
                className="h-11 rounded-xl"
              />
              <Input
                data-testid="contact-phone"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                placeholder="Phone"
                className="h-11 rounded-xl"
              />
              <Input
                data-testid="contact-email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                placeholder="Email (optional)"
                className="h-11 rounded-xl"
              />
              <Textarea
                data-testid="order-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes for the barista (optional)"
                className="rounded-xl"
                rows={2}
              />
            </div>

            <Button
              data-testid="place-order"
              onClick={placeOrder}
              disabled={submitting || cart.lines.length === 0}
              className="w-full bg-[#121614] hover:bg-black text-white rounded-full h-12"
            >
              {submitting ? "Placing…" : `Place order · ${money(cart.totalAmount)}`}
            </Button>
          </div>
        </aside>
      </section>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Reusable bits                                                            */
/* ─────────────────────────────────────────────────────────────────────── */

function QtyStepper({ value, onChange, testidPrefix }) {
  return (
    <div className="inline-flex items-center rounded-full border border-[#E8E5DF] bg-white">
      <button
        type="button"
        data-testid={`${testidPrefix}-minus`}
        onClick={() => onChange(Math.max(1, Number(value) - 1))}
        className="h-10 w-10 inline-flex items-center justify-center hover:bg-[#F3F0EA] rounded-l-full"
        aria-label="Decrease quantity"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span
        data-testid={`${testidPrefix}-value`}
        className="w-10 text-center font-medium tabular-nums"
      >
        {value}
      </span>
      <button
        type="button"
        data-testid={`${testidPrefix}-plus`}
        onClick={() => onChange(Math.min(20, Number(value) + 1))}
        className="h-10 w-10 inline-flex items-center justify-center hover:bg-[#F3F0EA] rounded-r-full"
        aria-label="Increase quantity"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

function AllergyChips({ item, testidPrefix }) {
  const allergens = detectAllergens(item);
  if (allergens.length === 0) return null;
  return (
    <div
      data-testid={`${testidPrefix}-allergy`}
      className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px]"
    >
      <AlertTriangle className="h-3.5 w-3.5 text-amber-700 shrink-0" strokeWidth={2} />
      <span className="font-bold uppercase tracking-wider text-amber-800">Contains:</span>
      {allergens.map((a) => (
        <span
          key={a}
          data-testid={`${testidPrefix}-allergy-${a}`}
          className="inline-flex items-center rounded-full bg-amber-200/60 text-amber-900 px-2 py-0.5 font-medium"
        >
          {a}
        </span>
      ))}
    </div>
  );
}

function CartPanel({ cart }) {
  return (
    <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingBag className="h-4 w-4 text-[#C05746]" />
        <h2 className="font-serif-display text-xl">Your cart</h2>
        <span className="ml-auto text-xs text-[#5C625E]" data-testid="cart-count">
          {cart.totalItems} item{cart.totalItems === 1 ? "" : "s"}
        </span>
      </div>
      {cart.lines.length === 0 ? (
        <p className="text-sm text-[#5C625E]" data-testid="cart-empty">
          No items yet — build a coffee or add a snack.
        </p>
      ) : (
        <ul className="divide-y divide-[#E8E5DF]" data-testid="cart-lines">
          {cart.lines.map((l, i) => {
            const summary = (l.selected_addons || []).map((a) => a.name).join(" · ");
            return (
              <li
                key={i}
                data-testid={`cart-line-${i}`}
                className="py-3 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{l.name}</p>
                  {summary && <p className="text-xs text-[#5C625E] mt-0.5">{summary}</p>}
                  <div className="mt-2">
                    <QtyStepper
                      value={l.quantity}
                      onChange={(n) => cart.updateQuantity(i, n)}
                      testidPrefix={`cart-line-qty-${i}`}
                    />
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <p className="text-sm font-medium tabular-nums">{money(l.line_total)}</p>
                  <button
                    type="button"
                    data-testid={`cart-remove-${i}`}
                    onClick={() => cart.removeLine(i)}
                    className="text-[#5C625E] hover:text-[#C05746]"
                    aria-label="Remove line"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {cart.lines.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#E8E5DF] flex items-center justify-between text-sm">
          <span className="text-[#5C625E]">Total</span>
          <span className="font-medium tabular-nums" data-testid="cart-total">{money(cart.totalAmount)}</span>
        </div>
      )}
    </div>
  );
}
